import { ISession } from 'models/session';
import { IModule } from 'models/permission';
import { OperationTypes } from 'utils/enums/system-template';
import { ISite } from 'models/site';
import { ModelsNames } from 'utils/enums/model-names';

export enum AuthActions {
  Logout = '[Auth] Logout',
  AutoLogin = '[Auth] Auto Login',
  SetSession = '[Auth] Set Session',
  AddSessionSite = '[Auth] Add Session Site',
  UpdateSessionSite = '[Auth] Update Session Site',
  SetAccessToken = '[Auth] Set AccessToken',
  SetSettings = '[Auth] Set Settings',
  SetPermissions = '[Auth] Set Permissions',
  SetSwitchViewSideEffectsHandler = '[Auth] Set Switch View Side Effects Handler',
  SetView = '[Auth] Set View Company and Sites',
  SetIntegrationEnabled = '[Auth] Set Integration Enabled',
}

// type
export type Logout = {
  type: AuthActions.Logout;
};

export type AutoLogin = {
  type: AuthActions.AutoLogin;
};

export type SetSession = {
  type: AuthActions.SetSession;
  payload: { session: ISession };
};

export type AddSessionSite = {
  type: AuthActions.AddSessionSite;
  payload: { site: ISite };
};

export type UpdateSessionSite = {
  type: AuthActions.UpdateSessionSite;
  payload: { id: number; site: Partial<Omit<ISite, 'id'>> };
};

export type SetAccessToken = {
  type: AuthActions.SetAccessToken;
  payload: { accessToken: string; accessExpiration: number };
};

export type SetSettings = {
  type: AuthActions.SetSettings;
  payload: { name: string; value: string }[];
};

export type SetPermissions = {
  type: AuthActions.SetPermissions;
  payload: { permissions: IModule[] };
};

export type SetSwitchViewSideEffectsHandler = {
  type: AuthActions.SetSwitchViewSideEffectsHandler;
  payload: (() => void) | null;
};

export type SetIntegrationEnabled = {
  type: AuthActions.SetIntegrationEnabled;
  payload: { enabled: boolean };
};

export type SetView = {
  type: AuthActions.SetView;
  payload: {
    company: number;
    site: number;
    operationType: OperationTypes;
    isSchoolOperationsEnabled?: boolean;
    isEmployeeOperationsEnabled?: boolean;
    relatedEntity?: { name: ModelsNames; numericId: number };
  };
};

// Action creators
export const logout = (): Logout => {
  return {
    type: AuthActions.Logout,
  };
};

export const autoLogin = (): AutoLogin => {
  return {
    type: AuthActions.AutoLogin,
  };
};

export const setSession = (session: ISession): SetSession => {
  return {
    type: AuthActions.SetSession,
    payload: { session },
  };
};

export const addSessionSite = (site: ISite): AddSessionSite => {
  return {
    type: AuthActions.AddSessionSite,
    payload: { site: site },
  };
};

export const updateSessionSite = (id: number, site: Partial<Omit<ISite, 'id'>>): UpdateSessionSite => {
  return {
    type: AuthActions.UpdateSessionSite,
    payload: { id, site },
  };
};

export const setAccessToken = (accessToken: string, accessExpiration: number): SetAccessToken => {
  return {
    type: AuthActions.SetAccessToken,
    payload: { accessToken, accessExpiration },
  };
};

export const setSettings = (settings: { name: string; value: string }[]): SetSettings => {
  return {
    type: AuthActions.SetSettings,
    payload: settings,
  };
};

export const setPermissions = (permissions: IModule[]): SetPermissions => {
  return {
    type: AuthActions.SetPermissions,
    payload: { permissions },
  };
};

export const setView = (
  company: number,
  site: number,
  operationType: OperationTypes,
  isSchoolOperationsEnabled?: boolean,
  isEmployeeOperationsEnabled?: boolean,
  relatedEntity?: {
    name: ModelsNames;
    numericId: number;
  },
): SetView => {
  return {
    type: AuthActions.SetView,
    payload: { company, site, operationType, relatedEntity, isSchoolOperationsEnabled, isEmployeeOperationsEnabled },
  };
};

export const setSwitchViewSideEffectsHandler = (handler: (() => void) | null): SetSwitchViewSideEffectsHandler => {
  return {
    type: AuthActions.SetSwitchViewSideEffectsHandler,
    payload: handler,
  };
};

/**
 * This is a temporary solution to restrict certain capabilities in the system.
 * In the future, the proper approach will be to implement this via a feature flag
 */
export const setIntegrationEnabled = (enabled: boolean): SetIntegrationEnabled => {
  return {
    type: AuthActions.SetIntegrationEnabled,
    payload: { enabled },
  };
};

export type AuthTypes =
  | Logout
  | AutoLogin
  | SetSession
  | AddSessionSite
  | UpdateSessionSite
  | SetAccessToken
  | SetSettings
  | SetPermissions
  | SetView
  | SetSwitchViewSideEffectsHandler
  | SetIntegrationEnabled;
